import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UserProfileDisplay from '../components/UserProfileDisplay';
import { directoryAPI } from '../utils/api';
import { ZohoDirectoryUser } from '../types';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [directoryUser, setDirectoryUser] = useState<ZohoDirectoryUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userFullName, setUserFullName] = useState<string>('');

  const [userDepartment, setUserDepartment] = useState<string | null>(null);
  const [salesServiceLines, setSalesServiceLines] = useState<string[]>([]); // NEW
  const [loadingRole, setLoadingRole] = useState(true); // NEW

  const allServiceLines = [
    {
      id: 'dermatology',
      title: 'DERMATOLOGY',
      url: 'url(https://www.sagisdx.com/wp-content/uploads/2023/03/sagis-element-purple-2.jpg)'
    },
    {
      id: 'podiatry',
      title: 'PODIATRY',
      url: 'url(https://www.sagisdx.com/wp-content/uploads/2023/10/podiatric.webp)'
    }
  ];

  // Filter service lines based on salesServiceLines from role
  const getFilteredServiceLines = () => {
    // If loading, show nothing
    if (loadingRole) {
      return [];
    }
    
    // PRIORITY 1: Marketing/Innovation departments see ALL service lines (override any restrictions)
    if (userDepartment === 'Marketing' || userDepartment === 'Innovation') {
      return allServiceLines;
    }
    
    // PRIORITY 2: If user has specific service lines assigned, show only those
    if (salesServiceLines.length > 0) {
      return allServiceLines.filter(s => 
        salesServiceLines.includes(s.id)
      );
    }
    
    // PRIORITY 3: Default fallback - show dermatology only
    //return allServiceLines.filter(s => s.id === 'dermatology');

    // PRIORITY 3: No service lines assigned - return empty array
    // This will trigger the "No service lines available" message
    return [];
  };

  const serviceLines = getFilteredServiceLines();

  const handleServiceLineClick = (serviceLine: string) => {
    navigate(`/service/${serviceLine}`);
  };

  // Check authentication status and wait for directory data to be cached
  useEffect(() => {
    let pollingAttempts = 0;
    const maxPollingAttempts = 10; // Try for 5 seconds (10 attempts * 500ms)
    const pollingInterval = 500; // 500ms between attempts
    let pollingTimeout: NodeJS.Timeout;
    let finalCheckTimeout: NodeJS.Timeout;

    const checkDirectoryCache = async (): Promise<boolean> => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/status`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to check authentication status');
        }
        
        const data = await response.json();

        // Set user full name from auth status (available immediately)
        if (data.user?.fullName) {
          setUserFullName(data.user.fullName);
        }

        if (data.user?.department) {
        setUserDepartment(data.user.department);
        }
        
        // Check if directory cache has mobile number
        if (data.directoryCache?.hasMobile && data.directoryCache.mobile) {
          console.log('✅ Directory mobile found in cache:', data.directoryCache.mobile);
          setLoading(false);
          return true; // Stop polling
        } else if (data.directoryCache?.status === 'pending') {
          // Still processing in background
          console.log(`⏳ Directory profile still loading (attempt ${pollingAttempts + 1}/${maxPollingAttempts})`);
          return false; // Continue polling
        } else {
          // No directory cache at all - might be an error
          console.warn('⚠️ No directory cache found');
          return false; // Continue polling for a bit
        }
      } catch (err: any) {
        console.error('❌ Error checking directory cache:', err);
        return false; // Continue polling
      }
      
      return false;
    };

    const performFinalCheck = async () => {
      console.log('🔄 Performing final check after polling timeout...');
      
      try {
        // Check cache one more time
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/status`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.directoryCache?.hasMobile && data.directoryCache.mobile) {
            console.log('✅ Found mobile in final check!');
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.error('Final check error:', err);
      }
      
      // If we get here, data still isn't ready
      console.log('ℹ️ Data not available after final check');
      setLoading(false);
    };

    const startPolling = async () => {
      setLoading(true);
      setError(null);
      
      // Try immediately first
      const shouldStop = await checkDirectoryCache();
      
      if (shouldStop) {
        return; // Data loaded
      }
      
      // Start polling
      const poll = async () => {
        pollingAttempts++;
        
        if (pollingAttempts >= maxPollingAttempts) {
          // Timeout - but don't give up yet
          console.log('⏱️ Polling timeout - scheduling final check in 2 seconds...');
          
          // Schedule a final check after 2 more seconds
          // This gives the background fetch more time to complete
          finalCheckTimeout = setTimeout(performFinalCheck, 2000);
          return;
        }
        
        const shouldStop = await checkDirectoryCache();
        
        if (!shouldStop) {
          // Continue polling
          pollingTimeout = setTimeout(poll, pollingInterval);
        }
      };
      
      // Start first poll after initial delay
      pollingTimeout = setTimeout(poll, pollingInterval);
    };

    startPolling();

    // Cleanup function to clear timeouts if component unmounts
    return () => {
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
      if (finalCheckTimeout) {
        clearTimeout(finalCheckTimeout);
      }
    };
  }, []);

  // Fetch user role on component mount
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        setLoadingRole(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/user-role`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const roleData = await response.json();
          console.log('User role data:', roleData);
          
          setUserDepartment(roleData.department);
          setSalesServiceLines(roleData.salesServiceLines || []);
        }
      } catch (err) {
        console.error('Error fetching user role:', err);
      } finally {
        setLoadingRole(false);
      }
    };
    
    fetchUserRole();
  }, []);

  return (
    <div className="min-vh-100">
      {/* Background Video */}
      <video 
        className="video-background"
        autoPlay
        muted
        playsInline
        loop
        src="https://www.sagisdx.com/wp-content/uploads/2023/04/sagis-swirl-bg-3.mp4"
      />

      
      {/* Main Content (3 service lines)
      <div className="container container-homepage">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="row g-4">

              {/* Welcome Message
              {userFullName && (
                <h2 className="welcome-text-homepage text-center"> Welcome, {userFullName}!</h2>)
              }

              <p className="instruction-text-homepage lead mt-4 text-center">
                Please select a service line to begin
              </p>      

              {serviceLines.map((service) => (
                <div key={service.id} className="col-md-4">
                  <div 
                    className="card serviceline-card h-100"
                    onClick={() => handleServiceLineClick(service.id)}
                    style={{ 
                      backgroundImage: service.url,
                      backgroundSize: "cover",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center"
                    }}
                  >
                    <div className="card-body text-center p-4">
                      <div className="service-line-icon mb-3"></div>
                      <h4 className="card-title serviceline-text-homepage">{service.title}</h4>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>*/}

      {/* Main Content */}
      <div className="container container-homepage">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="row g-4 justify-content-center">
              <h4 className="welcome-text-homepage text-center">
                Please select a service line to begin
              </h4>      

              {loadingRole ? (
                // Show loader while fetching role
                <div className="col-12 text-center py-5">
                  <div className="spinner-border text-success" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading your service lines...</p>
                </div>
              ) : serviceLines.length === 0 ? (
                // Show message if no service lines available
                <div className="col-12 text-center py-5">
                  <h5 className="text-muted">No service lines available for your role</h5>
                  <p className="text-muted">Please contact your administrator if you believe this is an error.</p>
                </div>
              ) : (
                // Show service line cards
                serviceLines.map((service) => (
                  <div key={service.id} className="col-md-4">
                    <div 
                      className="card serviceline-card h-100"
                      onClick={() => handleServiceLineClick(service.id)}
                      style={{ 
                        backgroundImage: service.url,
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center"
                      }}
                    >
                      <div className="card-body text-center p-4">
                        <div className="service-line-icon mb-3"></div>
                        <h4 className="card-title serviceline-text-homepage">{service.title}</h4>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>    

        {/*User Profile Section - FOR TESTING 
        <div className="row mt-5">
          <div className="col-12">
            <UserProfileDisplay />
          </div>
        </div>
      </div>*/}

    </div>
  );
};

export default HomePage;
